import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from "@material-ui/core";
import CustomInput from "components/CustomInput";
import DatePicker from "components/DatePicker";
import DropZoneInDialog from "components/DropZoneInDialog";
import LoadingSpinner from "components/LoadingSpinner";
import NotificationProvider from "components/NotificationProvider";
import { useGetInvoiceVendorsQuery } from 'features/invoice/loads/loadInvoiceSlice';
import FadeInImage from "features/vehicles/components/FadeInImage";
import { IconDollar } from "icons";
import moment from "moment";
import { useEffect, useState } from "react";
import { vendorInvoiceStyles } from "../style";
import { VendorInvoiceStatus, VendorInvoiceStatusesList } from "../vendorInvoiceConsts";
import { useAddVendorInvoiceMutation, useUpdateVendorInvoiceMutation } from "../vendorInvoiceSlice";
import VendorInvoiceServices from "./VendorInvoiceServices";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import VendorInvoiceVehicles from "./VendorInvoiceVehicles";
import StatusCell from "./StatusCell";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { Alert, Box, Typography } from "@mui/material";
import InvoiceFormLayout from "../InvoiceFormLayout";


const VendorInvoiceForm = ({ open, onClose, invoice = {} }) => {
    const classes = vendorInvoiceStyles();

    let { data: qbVendors, isLoading: isLoadingQbVendors, error: qbVendorsError } = useGetInvoiceVendorsQuery();
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();
    qbVendors = qbVendors || [];
    vendors = vendors || [];

    const [addInvoice] = useAddVendorInvoiceMutation();
    const [updateInvoice] = useUpdateVendorInvoiceMutation();

    const [isSaving, setIsSaving] = useState(false);
    const [invoiceToEdit, setInvoiceToEdit] = useState(invoice || {});
    const [fileName, setFileName] = useState(invoice?.fileName || '');
    const [fileType, setFileType] = useState(invoice?.fileType || '');

    const editMode = invoiceToEdit.id > 0;
    const hasFormattedData = Array.isArray(invoiceToEdit?.vehicles)
         && invoiceToEdit.vehicles.some(v => v.isParsedFromInvoice === true);

    useEffect(() => {
        if (open) {
            setInvoiceToEdit(invoice || {});
            setFileName(invoice?.fileName || '');
            setFileType(invoice?.fileType || '');
        }
    }, [open, invoice?.id]);

    const onUploadFile = async (file) => {
        setFileName(file.name);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            setInvoiceToEdit(prev => ({
                ...prev,
                fileUrl: base64,
                fileName: file.name,
                fileType: file.type
            }));
        };
    };

    const onFieldChange = (field, val) => {
        setInvoiceToEdit(prev => ({
            ...prev,
            [field]: val
        }));
    };

    const onStatusChange = (statusId) => {
        onFieldChange('status', VendorInvoiceStatusesList.find(s => s.id == statusId)?.id);
        onFieldChange('statusId', statusId);
    };

    const onVehiclesChange = (vehicles) => {
        onFieldChange('vehicles', vehicles);
    };

    const invoiceFields = [
        {
            name: "Vendor Name",
            key: "vendorName",
            required: true,
        },
        {
            name: "QuickBooks Vendor",
            key: "qbVendorId",
            required: true,
            component: <CustomInput
                id="qbVendorId"
                label="QuickBooks Vendor"
                value={invoiceToEdit?.qbVendorId}
                onChange={(val) => onFieldChange('qbVendorId', val)}
                elementType="dropdown"
                required={true}
                values={qbVendors?.map(i => ({ ...i, name: i.displayName }))}
                showEmpty
            />
        },
        {
            name: "Parq Vendor",
            key: "vendorId",
            required: true,
            component: <CustomInput
                id="vendorId"
                label="Parq Vendor"
                value={invoiceToEdit?.vendorId}
                onChange={(val) => onFieldChange('vendorId', val)}
                elementType="dropdown"
                values={vendors}
                showEmpty
                required={true}
            />
        },
        {
            name: "Invoice Number",
            key: "invoiceNumber",
            required: true,
        },
        {
            name: "Invoice Date",
            key: "invoiceDate",
            required: true,
            component: <DatePicker
                title="Invoice Date"
                value={invoiceToEdit?.invoiceDate ? moment(invoiceToEdit?.invoiceDate).format("YYYY-MM-DD") : null}
                required={true}
                onSelect={(val) => onFieldChange('invoiceDate', val)} />
        },
        {
            name: 'Subtotal',
            key: 'subTotal',
            component: <CustomInput
                label='Subtotal'
                value={invoiceToEdit?.subTotal}
                onChange={(val) => {
                    onFieldChange('subTotal', val || 0)
                }}
                elementType="input"
                icon={<IconDollar />}
                numberOnly
            />
        },
        {
            name: 'Tax',
            key: 'tax',
            component: <CustomInput
                label='Tax'
                value={invoiceToEdit?.tax}
                onChange={(val) => {
                    onFieldChange('tax', val || 0)
                }}
                elementType="input"
                icon={<IconDollar />}
                numberOnly
            />
        },
        {
            name: 'Total Amount',
            key: 'total',
            component: <CustomInput
                label='Total Amount'
                value={invoiceToEdit?.total}
                onChange={(val) => {
                    onFieldChange('total', val || 0)
                }}
                elementType="input"
                icon={<IconDollar />}
                numberOnly
            />
        },
        {
            name: "Status",
            key: "statusId",
            component: <div style={{ marginTop: '1rem', fontSize: '1.2em' }}>
                <StatusCell row={invoiceToEdit} filled updateFunction={(val) => onStatusChange(val)} />
            </div>
        },
        {
            name: "Invoice Document",
            key: "invoiceDocumentData",
            component: <>
                {!editMode && (
                    <>
                        <DropZoneInDialog
                            labelDialog="Upload File"
                            buttonLabel={`${invoiceToEdit?.invoiceDocumentData || invoiceToEdit?.fileUrl ? 'Update' : 'Upload'} Invoice Document`}
                            actionButtonLabel="Save"
                            onSave={onUploadFile}
                            accept={{
                                'application/pdf': ['.pdf'],
                                'image/jpeg': ['.jpeg', '.jpg'],
                                'application/vnd.ms-excel': ['.xls'],
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                'text/csv': ['.csv']
                            }}
                        />

                        {(invoiceToEdit?.invoiceDocumentData || invoiceToEdit?.fileUrl) && (
                            <div className={classes.editDocument}>
                                <FadeInImage
                                    img={{ type: fileType, fileName: fileName, url: invoiceToEdit?.fileUrl }}
                                    isDocument={true}
                                    showFileName
                                />
                            </div>
                        )}
                    </>
                )}
            </>
        },
        {
            name: "Vehicles",
            key: 'vehicles',
            component: <VendorInvoiceVehicles
                vehicles={invoiceToEdit?.vehicles}
                onVehiclesChange={(val) => onVehiclesChange(val)}
            />

        },
        {
            name: "Services",
            key: 'services',
            component: <VendorInvoiceServices
                services={invoiceToEdit?.services}
                setServices={(val) => onFieldChange('services', val)}
                selectedVehicles={invoiceToEdit?.vehicles || []}
                invoiceToEdit={invoiceToEdit}
            />
        }
    ];

    const isValid = () => invoiceFields.every(field => !field.required || invoiceToEdit?.[field.key]);

    const onInvoiceSave = async () => {
        try {
            let result;
            setIsSaving(true);
            if (editMode) {
                result = await updateInvoice(invoiceToEdit);
                if (!result?.error)
                    NotificationProvider.success('Invoice updated successfully');
                else
                    NotificationProvider.error('Failed to update invoice');
            } else {
                result = await addInvoice(invoiceToEdit);
                if (!result?.error)
                    NotificationProvider.success('Invoice created successfully');
                else
                    NotificationProvider.error('Failed to create invoice');
            }
        } catch (error) {
            NotificationProvider.error('Failed to create invoice');
        } finally {
            onClose();
            setIsSaving(false);
        }
    };

    return (
        <>
            <LoadingSpinner loading={isSaving} />
            <Dialog
                maxWidth='full'
                fullWidth
                open={open}
                onClose={onClose}
            >
                <DialogTitle>{invoiceToEdit?.id ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
                <DialogContent>
                    {hasFormattedData && <Alert style={{ marginBottom: 16 }}
                        severity="warning" icon={<TipsAndUpdatesIcon />}
                    >
                        <Box>
                            <Typography fontWeight={500}>
                                The invoice was parsed using AI. AI can make mistakes — please review carefully.
                            </Typography>
                        </Box>
                    </Alert> }
                    {editMode ?
                        <InvoiceFormLayout
                            editMode={editMode}
                            fileUrl={invoiceToEdit?.fileUrl || invoiceToEdit?.invoiceDocumentData}
                            fileType={fileType}
                            fileName={fileName}
                        >
                            <InvoiceFields
                                invoiceFields={invoiceFields}
                                invoiceToEdit={invoiceToEdit}
                                onFieldChange={onFieldChange}
                            />
                        </InvoiceFormLayout> :
                        (
                            <InvoiceFields
                                invoiceFields={invoiceFields}
                                invoiceToEdit={invoiceToEdit}
                                onFieldChange={onFieldChange}
                            />
                        )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onInvoiceSave}
                        disabled={isSaving || !isValid()}
                        variant="contained"
                        color="primary"
                    >
                        {isSaving ? "Saving..." : (invoiceToEdit?.id ? "Save" : "Create")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VendorInvoiceForm;

    const InvoiceFields = ({ invoiceFields, invoiceToEdit, onFieldChange }) => (
        <Grid container direction="column" spacing={2}>
            {invoiceFields?.map((field, index) => (
                <Grid item key={index}>
                    {field.component ?? (
                        <CustomInput
                            id={field.key}
                            label={field.name}
                            value={invoiceToEdit?.[field.key] ?? ''}
                            elementType="input"
                            onChange={(val) => onFieldChange(field.key, val)}
                            required={field.required}
                        />
                    )}
                </Grid>
            ))}
        </Grid>
    );
